import { useAppDispatch } from "@app/hooks"
import GenericDialog from "@cmp/genericDialog"
import { ProgramDomain, TrackDto } from "@masterschool/course-builder-api"
import { Autocomplete, Stack, TextField } from "@mui/material"
import { useState } from "react"
import EditorTextField from "../../editor/components/editorTextField"
import { saveTrack, updateTrackMetadata } from "@features/tracks/trackSlice"
import { showSuccessSnackbar } from "@features/ui/uiSlice"
import { domainToDisplayName } from "@utils/domainUtils"

export const EditTrackDetailsPopup = (props: {
  track: TrackDto
  onClose: () => void
}) => {
  const { track, onClose } = props
  const dispatch = useAppDispatch()
  const [internalName, setInternalName] = useState<string>(
    track.internalName ?? "",
  )
  const [externalName, setExternalName] = useState<string>(
    track.externalName ?? "",
  )
  const [domain, setDomain] = useState<string | undefined>(track.domain)
  const [durationInMonths, setDurationInMonths] = useState<number | undefined>(
    track.durationInMonths,
  )
  const [displayId, setDisplayId] = useState<string | undefined>(
    track.displayId,
  )
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const detailsChanged =
    internalName !== (track.internalName ?? "") ||
    externalName !== (track.externalName ?? "") ||
    domain !== track.domain
  const metadataChanged =
    displayId !== track.displayId || durationInMonths !== track.durationInMonths
  const hasChanged = detailsChanged || metadataChanged

  const onSubmit = async () => {
    // A duration is mandatory — a 0/empty duration is what makes a program read
    // as "ended". Display Id is no longer required (it blocked fixing the very
    // tracks that need a duration). Editing details creates a draft if the track
    // is published, exactly like any other track edit.
    if (!durationInMonths || durationInMonths < 1) {
      setError("Duration must be at least 1 month.")
      return
    }
    if (internalName.trim().length === 0) {
      setError("Internal name is required.")
      return
    }
    setIsLoading(true)
    setError(undefined)
    try {
      if (detailsChanged) {
        await dispatch(
          saveTrack({
            trackId: track.id,
            track: {
              ...track,
              internalName: internalName.trim(),
              externalName: externalName.trim() || undefined,
              domain,
            },
          }),
        ).unwrap()
      }
      if (metadataChanged) {
        await dispatch(
          updateTrackMetadata({
            trackId: track.id,
            metadata: { durationInMonths, displayId },
          }),
        ).unwrap()
      }
      onClose()
      dispatch(showSuccessSnackbar("Track details updated"))
    } catch {
      setError("Could not save the track. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <GenericDialog
      title="Edit track details"
      onClose={onClose}
      size="xs"
      content={
        <Stack gap={2}>
          <EditorTextField
            label="Internal name"
            value={internalName}
            onChange={(e) => setInternalName(e.target.value)}
          />
          <EditorTextField
            label="Student-facing name"
            value={externalName}
            onChange={(e) => setExternalName(e.target.value)}
          />
          <Autocomplete
            options={Object.values(ProgramDomain) as string[]}
            value={domain ?? null}
            onChange={(_, newValue) => setDomain(newValue ?? undefined)}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" label="School" />
            )}
            renderOption={(optionProps, option) => (
              <li {...optionProps} key={option}>
                {domainToDisplayName(option)}
              </li>
            )}
            getOptionLabel={(option) => domainToDisplayName(option)}
          />
          <EditorTextField
            label="Display Id"
            value={displayId || ""}
            onChange={(e) => {
              setDisplayId(e.target.value)
            }}
          />
          <EditorTextField
            type="number"
            label="Duration in months"
            value={durationInMonths || ""}
            onChange={(e) => {
              const parsed = parseInt(e.target.value)
              setDurationInMonths(Number.isNaN(parsed) ? undefined : parsed)
            }}
            InputProps={{
              inputProps: {
                min: 1,
              },
            }}
            error={!!error}
            helperText={error}
          />
        </Stack>
      }
      buttons={[
        {
          text: "Cancel",
          type: "secondary",
          onClick: onClose,
        },
        {
          loading: isLoading,
          text: "Save",
          type: "primary",
          onClick: onSubmit,
          disabled: !hasChanged,
        },
      ]}
      open={true}
    />
  )
}
